import type { PersonInvolvement } from "../types/involvement.js";
import type { OrganisationId } from "../types/organisation.js";
import { organisationsInvolvedWithPerson } from "./organisationsInvolvedWithPerson.js";

/**
 * The organisations that are part of this person's care at this instant, plus
 * the one the session is acting for.
 *
 * What "near" means in Haven. eMHA judges distance against data sharing
 * agreements between data controllers; Haven has neither, and the configuration
 * chain is deliberately not one: it gives no reach and no rule walks it. The
 * question this product can answer instead is the one it is actually asking:
 * is that party part of this person's care?
 *
 * Live involvements only. An ended one means that organisation is no longer
 * part of the person's care, which is exactly when starting an involvement for
 * one of its users deserves a second look, and counting every organisation that
 * had ever been involved would make the warning go quiet for good on a
 * long-running case.
 */
export const organisationsNearPerson = ({
  involvements,
  actingOrganisationId,
  now = new Date(),
}: {
  involvements: readonly Pick<
    PersonInvolvement,
    "party" | "endedAt" | "expiresAt"
  >[];
  actingOrganisationId: OrganisationId;
  now?: Date;
}): OrganisationId[] => [
  actingOrganisationId,
  ...organisationsInvolvedWithPerson({ involvements, now }),
];
